import { PrismicRichText } from '@prismicio/react';
import { ButtonInline } from '@remote-com/norma/core/button';
import ArrowRightSvg from '@remote-com/norma/icons/arrow-right.svg';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fieldHasContent, transformProperties } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { Title, Description } from './InvoiceSlice';

import { SectionLabel } from '@/src/components/OverviewSection/OverviewSectionRow';
import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { SectionHeader } from '@/src/views/Services/GlobalPayroll/slices/BestPracticesSlice';

const Section = styled.section`
  ${fluidProp(['padding-bottom', 'padding-top'], '50px', '100px')}
  background-image: var(--colors-zircon);

  ${Title} {
    max-width: unset;
  }

  ${Description} {
    color: var(--colors-darkBlue);
  }
`;

const SectionContent = styled.div`
  background-color: var(--colors-blank);
  padding: 32px 40px;
  border-radius: 20px;
  display: flex;

  ${tabletBreakpoint(css`
    flex-direction: column;
  `)}
`;

const ImageWrapper = styled.div`
  min-width: 150px;
  margin-right: 50px;

  ${tabletBreakpoint(css`
    margin-bottom: 50px;
    margin-right: 0;
  `)}
`;

const ReadMoreWrapper = styled.div`
  ${({ theme }) => theme.typography.bodyLeadBold}
  color: var(--colors-spindle);
  padding: 20px 0 6px;
`;

const Link = styled(ButtonInline)`
  && {
    color: var(--colors-darkBlue);
    ${({ theme }) => theme.typography.bodyLeadMedium}
    margin-bottom: 6px;
    display: flex;
    justify-content: flex-start;
  }
`;

const ArrowIcon = styled(ArrowRightSvg)`
  width: 18px;
  margin-left: 16px;
  fill: var(--colors-turquoise);
`;

export function ResourcesSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { label, title, description, cardDescription, illustration, readMore } = primary;

  return (
    <Section>
      <ContentContainer>
        <SectionHeader>
          {fieldHasContent(label) && <SectionLabel>{label}</SectionLabel>}
          {fieldHasContent(title) && (
            <Title>
              <PrismicRichText field={title} />
            </Title>
          )}
          {fieldHasContent(description) && (
            <Description>
              <PrismicRichText field={description} />
            </Description>
          )}
        </SectionHeader>
        <SectionContent>
          <ImageWrapper>
            <Image
              src={illustration?.url}
              alt={illustration?.alt || ''}
              width={illustration?.dimensions.width}
              height={illustration?.dimensions.height}
            />
          </ImageWrapper>
          <div>
            {fieldHasContent(cardDescription) && (
              <Description>
                <PrismicRichText field={cardDescription} />
              </Description>
            )}
            {fields && (
              <>
                <ReadMoreWrapper> {readMore || 'Read more'}</ReadMoreWrapper>
                {fields.map(({ ctaLabel, ctaLink }, index) => (
                  <div key={index}>
                    {ctaLabel && ctaLink && (
                      <Link href={ctaLink?.url} target={ctaLink?.target}>
                        {ctaLabel}
                        <ArrowIcon />
                      </Link>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </SectionContent>
      </ContentContainer>
    </Section>
  );
}
